import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import CompanyHeader from "./FinancialS_Components/CompanyHeader";
import "./FinancialStatementsNotes.scss";

const normalizeKeys = (value) => {
  if (Array.isArray(value)) return value.map(normalizeKeys);
  if (value && typeof value === "object") {
    return Object.entries(value).reduce((acc, [key, nested]) => {
      acc[key.charAt(0).toLowerCase() + key.slice(1)] = normalizeKeys(nested);
      return acc;
    }, {});
  }
  return value;
};

const formatAmount = (value) => {
  const n = Number(value ?? 0);
  const formatted = Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `(${formatted})` : formatted;
};

const fmtDate = (year) => `31.12.${year}`;

const movementText = (movement) => {
  const numeric = Number(movement ?? 0);
  if (Math.abs(numeric) < 0.005) {
    return "nuk ka ndryshim material ndaj vitit paraprak";
  }
  if (numeric > 0) {
    return `është rritur për ${formatAmount(numeric)} ndaj vitit paraprak`;
  }
  return `është ulur për ${formatAmount(Math.abs(numeric))} ndaj vitit paraprak`;
};

const buildDetailedNarrative = (note, cyB, pyB, cyI, pyI, fiscalYear) => {
  const current = Number(note.current ?? 0);
  const prior = Number(note.prior ?? 0);
  const movement = current - prior;
  const base = [
    `Shpalosja nr. ${note.noteNumber} paraqet pozicionin '${note.title}' sipas ${note.basis}.`,
    `Vlera me ${fmtDate(fiscalYear)} është ${formatAmount(current)}, ndërsa me ${fmtDate(fiscalYear - 1)} është ${formatAmount(prior)}.`,
    `Pozicioni ${movementText(movement)}.`,
  ];

  if (note.noteNumber === 29) {
    base.push(
      `Fitimi i bartur lidhet drejtpërdrejt me rezultatin e periudhës: fitimi neto i raportuar në Pasqyrën e të Ardhurave është ${formatAmount(cyI.fitimiNeto)} dhe rezultati para tatimit është ${formatAmount(cyI.fitimiParaTatimit)}.`
    );
  }

  if (note.noteNumber === 28) {
    base.push(
      `Ndryshimi në kapital reflekton kontributet/tërheqjet e pronarit dhe harmonizohet me Pasqyrën e Ndryshimeve në Ekuitet (kapitali i pronarit në fund të periudhës ${formatAmount(cyB.kapitaliPronarit)}).`
    );
  }

  if (note.noteNumber === 30) {
    base.push(
      `Rezervat ndikohen nga transferet nga fitimi i periudhës dhe nga politika e shpërndarjes së fitimit; gjithsej të ardhurat gjithëpërfshirëse janë ${formatAmount(cyI.gjithsejGjitheperfshirese)}.`
    );
  }

  if (note.noteNumber >= 15 && note.noteNumber <= 27) {
    base.push(
      `Ky zë shpalos strukturën e detyrimeve dhe ndihmon lexuesin të vlerësojë afatin e pagesave (afatshkurtër/afatgjatë) dhe ndikimin në likuiditet.`
    );
  }

  if (note.noteNumber >= 1 && note.noteNumber <= 14) {
    base.push(
      `Ky zë shpjegon përbërjen e pasurive dhe burimin e vlerës së raportuar në Bilancin e Gjendjes.`
    );
  }

  if (note.noteNumber >= 31 && note.noteNumber <= 40) {
    base.push(
      `Ky zë i përket Pasqyrës së të Ardhurave dhe përdoret për të shpjeguar burimin e rezultatit financiar të periudhës.`
    );
  }

  if (note.noteNumber === 31) {
    base.push(
      `Të hyrat paraqesin bazën e gjenerimit të fitimit neto; krahasuar me koston e shitjes (${formatAmount(cyI.kmsh)}), ato formojnë fitimin bruto (${formatAmount(cyI.fitimiBruto)}).`
    );
  }

  if (note.noteNumber === 39) {
    base.push(
      `Shpenzimi i tatimit në fitim lidhet me rezultatin para tatimit (${formatAmount(cyI.fitimiParaTatimit)}), ndërsa fitimi neto i vitit pas tatimit është ${formatAmount(cyI.fitimiNeto)}.`
    );
  }

  if (note.noteNumber === 40) {
    base.push(
      `Diferencat këmbimore paraqesin element të të ardhurave tjera gjithëpërfshirëse dhe ndikojnë në shumën përfundimtare gjithëpërfshirëse (${formatAmount(cyI.gjithsejGjitheperfshirese)}).`
    );
  }

  if (note.noteNumber >= 31 && note.noteNumber <= 40) {
    const priorNet = Number(pyI.fitimiNeto ?? 0);
    const currentNet = Number(cyI.fitimiNeto ?? 0);
    base.push(
      `Ndikimi kumulativ i këtyre zërave reflektohet në fitimin neto: ${formatAmount(currentNet)} për periudhën aktuale kundrejt ${formatAmount(priorNet)} për periudhën paraprake.`
    );
  }

  return base.join(" ");
};

function mapNotes(cyBalance, pyBalance, cyIncome, pyIncome, fiscalYear) {
  const cyB = cyBalance ?? {};
  const pyB = pyBalance ?? {};
  const cyI = cyIncome ?? {};
  const pyI = pyIncome ?? {};

  const notes = [
    { noteNumber: 1, title: "Paraja dhe ekuivalentet e parasë", current: cyB.paraja, prior: pyB.paraja, basis: "IAS 7" },
    { noteNumber: 2, title: "Investimet financiare afatshkurtera", current: cyB.investimetFinanciareAfatshkurtera, prior: pyB.investimetFinanciareAfatshkurtera, basis: "IFRS 9" },
    { noteNumber: 3, title: "Llogaritë e arkëtueshme", current: cyB.llogariteArketueshme, prior: pyB.llogariteArketueshme, basis: "IFRS 9" },
    { noteNumber: 4, title: "Stoqet", current: cyB.stoqet, prior: pyB.stoqet, basis: "IAS 2" },
    { noteNumber: 5, title: "Pasuritë tjera afatshkurtera", current: cyB.pasuriteTjeraAfatshkurtera, prior: pyB.pasuriteTjeraAfatshkurtera, basis: "IAS 1" },
    { noteNumber: 6, title: "Investimet në pjesëmarrje", current: cyB.investimetPjesmarrje, prior: pyB.investimetPjesmarrje, basis: "IAS 28" },
    { noteNumber: 7, title: "Investimet në filial", current: cyB.investimetFiliale, prior: pyB.investimetFiliale, basis: "IFRS 10" },
    { noteNumber: 8, title: "Investimet financiare afatgjata", current: cyB.investimetFinanciareAfatgjata, prior: pyB.investimetFinanciareAfatgjata, basis: "IFRS 9" },
    { noteNumber: 9, title: "Prona, pajisjet dhe impiantet", current: cyB.pronaPaisjetImpiantet, prior: pyB.pronaPaisjetImpiantet, basis: "IAS 16" },
    { noteNumber: 10, title: "Pronat investuese", current: cyB.pronaInvestuese, prior: pyB.pronaInvestuese, basis: "IAS 40" },
    { noteNumber: 11, title: "Investimet në vijim", current: cyB.investimetNeVijim, prior: pyB.investimetNeVijim, basis: "IAS 16" },
    { noteNumber: 12, title: "Pasuritë e paprekshme", current: cyB.pasuritePaprekshme, prior: pyB.pasuritePaprekshme, basis: "IAS 38" },
    { noteNumber: 13, title: "Pasuritë e shtyra tatimore", current: cyB.pasuriteShtyraTatimore, prior: pyB.pasuriteShtyraTatimore, basis: "IAS 12" },
    { noteNumber: 14, title: "Pasuritë tjera afatgjata", current: cyB.pasuriteTjeraAfatgjata, prior: pyB.pasuriteTjeraAfatgjata, basis: "IAS 1" },
    { noteNumber: 15, title: "Mbitërheqja bankare", current: cyB.mbiterheqjaBankare, prior: pyB.mbiterheqjaBankare, basis: "IFRS 7" },
    { noteNumber: 16, title: "Llogaritë e pagueshme", current: cyB.llogaritePagueshmeTjera, prior: pyB.llogaritePagueshmeTjera, basis: "IAS 1" },
    { noteNumber: 17, title: "Kreditë/Huatë afatshkurtera", current: cyB.krediteHuateAfatshkurter, prior: pyB.krediteHuateAfatshkurter, basis: "IFRS 9" },
    { noteNumber: 18, title: "Interesi i pagueshëm", current: cyB.interesiPagueshme, prior: pyB.interesiPagueshme, basis: "IAS 1" },
    { noteNumber: 19, title: "Tatimi në fitim i pagueshëm", current: cyB.tatimiFitimPagueshem, prior: pyB.tatimiFitimPagueshem, basis: "IAS 12" },
    { noteNumber: 20, title: "Provizionet afatshkurta", current: cyB.provizionetAfatshkurta, prior: pyB.provizionetAfatshkurta, basis: "IAS 37" },
    { noteNumber: 21, title: "Detyrimet e lizingut afatshkurter", current: cyB.detyrimetLizingutAfatshkurter, prior: pyB.detyrimetLizingutAfatshkurter, basis: "IFRS 16" },
    { noteNumber: 22, title: "Detyrime tjera afatshkurtera", current: cyB.detyrimetTjeraAfatshkurtera, prior: pyB.detyrimetTjeraAfatshkurtera, basis: "IAS 1" },
    { noteNumber: 23, title: "Kreditë/Huatë afatgjata", current: cyB.krediteHuateAfatgjata, prior: pyB.krediteHuateAfatgjata, basis: "IFRS 9" },
    { noteNumber: 24, title: "Provizionet afatgjata", current: cyB.provizionetAfatgjata, prior: pyB.provizionetAfatgjata, basis: "IAS 37" },
    { noteNumber: 25, title: "Detyrimet e lizingut afatgjatë", current: cyB.detyrimetLizingutAfatgjate, prior: pyB.detyrimetLizingutAfatgjate, basis: "IFRS 16" },
    { noteNumber: 26, title: "Detyrimet e shtyra tatimore", current: cyB.detyrimetShtyraTatimore, prior: pyB.detyrimetShtyraTatimore, basis: "IAS 12" },
    { noteNumber: 27, title: "Detyrime tjera afatgjata", current: cyB.detyrimetTjeraAfatgjata, prior: pyB.detyrimetTjeraAfatgjata, basis: "IAS 1" },
    { noteNumber: 28, title: "Kapitali i pronarit", current: cyB.kapitaliPronarit, prior: pyB.kapitaliPronarit, basis: "IAS 1" },
    { noteNumber: 29, title: "Fitimi i bartur", current: cyB.fitimiBartur, prior: pyB.fitimiBartur, basis: "IAS 1" },
    { noteNumber: 30, title: "Rezervat e tjera", current: cyB.rezervatTjera, prior: pyB.rezervatTjera, basis: "IAS 1" },
    { noteNumber: 31, title: "Të hyrat", current: cyI.teArdhurat, prior: pyI.teArdhurat, basis: "IFRS 15" },
    { noteNumber: 32, title: "Kostoja e shitjes (KMSH)", current: cyI.kmsh, prior: pyI.kmsh, basis: "IAS 2" },
    { noteNumber: 33, title: "Të ardhurat tjera", current: cyI.teArdhuraTjera, prior: pyI.teArdhuraTjera, basis: "IAS 1" },
    { noteNumber: 34, title: "Shpenzimet e shpërndarjes", current: cyI.shpenzimetShperndarjes, prior: pyI.shpenzimetShperndarjes, basis: "IAS 1" },
    { noteNumber: 35, title: "Shpenzimet administrative", current: cyI.shpenzimetAdministrative, prior: pyI.shpenzimetAdministrative, basis: "IAS 1" },
    { noteNumber: 36, title: "Shpenzimet e tjera", current: cyI.shpenzimetTjera, prior: pyI.shpenzimetTjera, basis: "IAS 1" },
    { noteNumber: 37, title: "Shpenzimet financiare", current: cyI.shpenzimetFinanciare, prior: pyI.shpenzimetFinanciare, basis: "IFRS 7" },
    { noteNumber: 38, title: "Të ardhurat financiare", current: cyI.teArdhuratFinanciare, prior: pyI.teArdhuratFinanciare, basis: "IFRS 7" },
    { noteNumber: 39, title: "Shpenzimet e tatimit në fitim", current: cyI.tatimiFitim, prior: pyI.tatimiFitim, basis: "IAS 12" },
    { noteNumber: 40, title: "Diferenca këmbimore", current: cyI.gjithsejGjitheperfshirese - cyI.fitimiNeto, prior: pyI.gjithsejGjitheperfshirese - pyI.fitimiNeto, basis: "IAS 21" },
  ];

  return notes.map((note) => {
    const current = Number(note.current ?? 0);
    const prior = Number(note.prior ?? 0);
    return {
      ...note,
      detail: buildDetailedNarrative(note, cyB, pyB, cyI, pyI, fiscalYear),
      movement: current - prior,
      netResult: cyI.fitimiNeto,
    };
  });
}

export default function FinancialStatementsNotes() {
  const { fiscalYear } = useContext(FiscalYearContext);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isGeneratingPrint, setIsGeneratingPrint] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!fiscalYear || fiscalYear <= 0) {
        setError("Viti fiskal nuk është përcaktuar saktë.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const headers = authHeader();
        const [balanceRes, incomeRes, companyRes] = await Promise.all([
          axios.get(ApiLink.API_URL + "BalanceSheet/getBalanceSheet", {
            headers,
            params: { fiscalYear },
          }),
          axios.get(ApiLink.API_URL + "BalanceSheet/getIncomeStatement", {
            headers,
            params: { fiscalYear },
          }),
          axios.get(ApiLink.API_URL + "Companies/getCompanyDetails", { headers }),
        ]);

        const balance = normalizeKeys(balanceRes.data);
        const income = normalizeKeys(incomeRes.data);

        setNotes(
          mapNotes(
            balance?.currentYear,
            balance?.priorYear,
            income?.currentYear,
            income?.priorYear,
            Number(fiscalYear)
          )
        );

        if (Array.isArray(companyRes.data) && companyRes.data.length > 0) {
          setCompanyInfo(companyRes.data[0]);
        } else {
          setCompanyInfo(null);
        }
      } catch (loadError) {
        console.error("Gabim në marrjen e shpalosjeve financiare:", loadError);
        setError(loadError.response?.data?.message || "Gabim në marrjen e të dhënave");
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [fiscalYear]);

  const introText = useMemo(() => {
    const companyName = companyInfo?.CompanyName || "Kompania";
    return `Këto shpalosje janë të lidhura drejtpërdrejt me numrat 1-30 të Bilancit të Gjendjes dhe 31-40 të Pasqyrës së të Ardhurave, duke u bazuar në vlerat aktuale të raportuara për ${companyName}.`;
  }, [companyInfo]);

  const buildPdfDoc = () => {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const priorFiscalYear = Number(fiscalYear) - 1;
    const periodDate = `31.12.${fiscalYear}`;

    const bodyRows = notes.map((note) => [
      String(note.noteNumber),
      note.title || "",
      note.basis || "",
      formatAmount(note.current),
      formatAmount(note.prior),
      note.detail || "",
    ]);

    doc.setTextColor(25, 25, 25);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(companyInfo?.CompanyName || "Kompania", 105, 18.5, { align: "center" });

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(`Shpalosjet e pasqyrave financiare me date ${periodDate}`, 105, 23.5, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.text("Notes to the Financial Statements", 105, 27.5, { align: "center" });

    doc.setFontSize(8);
    doc.text(`Numri fiskal: ${companyInfo?.IdentificationNumber || "-"}`, 10, 32.5);
    doc.text(`Periudha: ${periodDate}`, 200, 32.5, { align: "right" });

    autoTable(doc, {
      startY: 38,
      head: [["Nr.", "Shpalosja", "Standardi", `31.12.${fiscalYear}`, `31.12.${priorFiscalYear}`, "Pershkrimi"]],
      body: bodyRows,
      theme: "grid",
      styles: {
        fontSize: 7.2,
        cellPadding: 0.95,
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
        textColor: [33, 33, 33],
      },
      headStyles: {
        fillColor: [40, 73, 69],
        textColor: [255, 255, 255],
        halign: "center",
        valign: "middle",
        fontStyle: "bold",
        lineColor: [40, 73, 69],
        lineWidth: 0.2,
      },
      columnStyles: {
        0: { cellWidth: 10, halign: "center" },
        1: { cellWidth: 40 },
        2: { cellWidth: 18, halign: "center" },
        3: { cellWidth: 22, halign: "right" },
        4: { cellWidth: 22, halign: "right" },
        5: { cellWidth: 86 },
      },
      didParseCell: (hookData) => {
        if (hookData.section !== "body") {
          return;
        }

        if (hookData.column.index === 5) {
          hookData.cell.styles.fontSize = 7;
        }
      },
      margin: { top: 8, right: 6, bottom: 8, left: 6 },
      pageBreak: "auto",
    });

    let signatureY = (doc.lastAutoTable?.finalY || 40) + 20;
    const pageHeight = doc.internal.pageSize.getHeight();

    if (signatureY > pageHeight - 22) {
      doc.addPage();
      signatureY = 36;
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(50, 50, 50);
    doc.text("Pergatiti", 42, signatureY - 7, { align: "center" });
    doc.text("Miratoi", 148, signatureY - 7, { align: "center" });
    doc.line(20, signatureY, 85, signatureY);
    doc.line(115, signatureY, 180, signatureY);

    const totalPages = doc.internal.getNumberOfPages();
    for (let page = 1; page <= totalPages; page += 1) {
      doc.setPage(page);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Faqe ${page}/${totalPages}`, 6, 292);
      doc.text("Gjeneruar nga Accountree", 200, 292, { align: "right" });
    }

    const companySlug = (companyInfo?.CompanyName || "Kompania")
      .trim()
      .replace(/\s+/g, "_");

    return { doc, companySlug };
  };

  const handleDownloadPdf = async () => {
    if (isGeneratingPdf || isLoading || notes.length === 0) {
      return;
    }
    setIsGeneratingPdf(true);
    try {
      const { doc, companySlug } = buildPdfDoc();
      doc.save(`Shpalosjet_e_pasqyrave_financiare_${fiscalYear}_${companySlug}.pdf`);
    } catch (pdfError) {
      console.error("Gabim gjatë gjenerimit të PDF:", pdfError);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handlePrintPdf = async () => {
    if (isGeneratingPrint || isLoading || notes.length === 0) {
      return;
    }
    setIsGeneratingPrint(true);
    try {
      const { doc } = buildPdfDoc();
      const blobUrl = doc.output("bloburl");

      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = blobUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(blobUrl);
        }, 1000);
      };
    } catch (printError) {
      console.error("Gabim gjatë printimit:", printError);
    } finally {
      setIsGeneratingPrint(false);
    }
  };

  if (error) {
    return (
      <div className="FSN__Page">
        <div className="FSN__Card">
          <div className="FSN__Intro">
            <h3>Gabim</h3>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="FSN__Page">
      <CompanyHeader
        reportTitle={`Shpalosjet e pasqyrave financiare më ${fmtDate(fiscalYear)}`}
        reportSubtitle="Notes to the Financial Statements"
        fiscalYear={fiscalYear}
        onDownloadPdf={handleDownloadPdf}
        isGeneratingPdf={isGeneratingPdf}
        onPrint={handlePrintPdf}
        isGeneratingPrint={isGeneratingPrint}
      />

      <div className="FSN__Card">
        <div className="FSN__Intro">
          <h3>Shpalosje sipas standardeve dhe numrave të pasqyrave</h3>
          <p>{introText}</p>
        </div>

        {isLoading ? (
          <div className="FSN__Loading">
            {[1, 2, 3, 4, 5].map((n) => (
              <div key={n} className="FSN__LoadingBar" />
            ))}
          </div>
        ) : (
          <div className="FSN__Accordion">
            {notes.map((note) => (
              <details key={note.noteNumber} className="FSN__Item" open={note.noteNumber <= 3}>
                <summary className="FSN__ItemSummary">
                  <div className="FSN__ItemLeft">
                    <span className="FSN__Chevron" aria-hidden="true">
                      ▾
                    </span>
                    <span className="FSN__Badge">{note.noteNumber}</span>
                    <span className="FSN__Title">{note.title}</span>
                  </div>
                  <span className="FSN__Amount">{formatAmount(note.current)}</span>
                </summary>
                <div className="FSN__Content">
                  <p>{note.detail}</p>
                </div>
              </details>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
